interface Coluna { nome: string }
interface Medida { nome: string; formato?: string | null }

/** Escapa para texto de HTML. */
const html = (texto: string) =>
  texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** O nome curto de uma coluna: "dLojas[UF]" vira "UF". */
const curta = (nome: string) => (nome.includes('[') ? nome.slice(nome.indexOf('[') + 1, nome.lastIndexOf(']')) : nome)

/**
 * Um relatório que já funciona com o dicionário: cartões com as primeiras medidas, um
 * gráfico por uma coluna e uma tabela.
 *
 * Não é para ir ao ar como está — é para a primeira prévia ter dado na tela e para
 * mostrar, num exemplo real, o jeito certo: tudo o que se pode trocar fica no bloco
 * portal-relatorio (medidas, eixo, títulos, rótulos, formatos, filtros da tela), o código
 * só usa os apelidos e desenha com Portal.bloco(id).desenhar, e há estados de vazio e erro. O cálculo fica no
 * modelo; o HTML só desenha.
 */
export function modeloInicial(nome: string, dicionario: { colunas: Coluna[]; medidas: Medida[] }): string {
  const medidas = dicionario.medidas.slice(0, 4).map(m => m.nome)
  const coluna = dicionario.colunas[0]?.nome
  const percentual = (m: string) => /%|percent|cumpr|share|taxa/i.test(m)
  const apelidos = medidas.map((_, i) => `m${i + 1}`)
  const porApelido = <T,>(f: (m: string, i: number) => T) => Object.fromEntries(medidas.map((m, i) => [apelidos[i], f(m, i)]))
  const formatos = porApelido(m => ({ tipo: percentual(m) ? 'percentual' : 'numero', casas: percentual(m) ? 1 : 0 }))

  const blocos: Record<string, unknown> = {}
  if (medidas.length) {
    blocos.numeros = {
      titulo: 'Resumo do período', forma: 'cartões',
      consulta: { medidas: porApelido(m => m) },
      rotulos: porApelido(m => m), formatos
    }
  }
  if (medidas.length && coluna) {
    blocos.grafico = {
      titulo: `${medidas[0]} por ${curta(coluna)}`, forma: 'barras',
      consulta: { colunas: { eixo: coluna }, medidas: { valor: medidas[0] }, ordenarPor: 'valor', direcao: 'DESC', limite: 12 },
      rotulos: { eixo: curta(coluna), valor: medidas[0] }, formatos: { valor: formatos.m1 }
    }
    blocos.tabela = {
      titulo: `Detalhe por ${curta(coluna)}`, forma: 'tabela',
      consulta: { colunas: { eixo: coluna }, medidas: porApelido(m => m), ordenarPor: 'm1', direcao: 'DESC', limite: 12 },
      rotulos: { eixo: curta(coluna), ...porApelido(m => m) }, formatos
    }
  }
  // Um filtro na tela, com a segunda coluna do dicionário (a primeira já é o eixo).
  const colunaFiltro = dicionario.colunas[1]?.nome
  const filtros_tela = colunaFiltro ? [{ id: 'filtro_1', coluna: colunaFiltro, rotulo: curta(colunaFiltro) }] : []
  const config = { textos: { titulo_pagina: nome, nota_rodape: 'Os números seguem o recorte e os filtros do portal.' }, filtros_tela, blocos }
  const json = JSON.stringify(config, null, 2).replace(/</g, '\\u003c').split('\n').map(l => '  ' + l).join('\n')

  const semDicionario = medidas.length === 0
    ? '  <p class="aviso">O dicionário deste relatório está vazio. Libere colunas e medidas na aba Dicionário e gere o modelo de novo.</p>\n'
    : ''

  return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${html(nome)}</title>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js"></script>
  <script type="application/json" id="portal-relatorio">
${json}
  </script>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 24px; font-family: "Segoe UI", system-ui, sans-serif; color: #0f172a; background: #f8fafc; }
    h1 { font-size: 20px; margin: 0 0 4px; }
    h2 { font-size: 13px; margin: 0 0 10px; color: #64748b; text-transform: uppercase; letter-spacing: .05em; }
    .sub { color: #64748b; font-size: 13px; margin-bottom: 20px; }
    .cartoes { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
    .cartao { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
    .rotulo { color: #64748b; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; }
    .valor { font-size: 24px; font-weight: 600; margin-top: 6px; }
    .painel { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 20px; }
    .bloco { margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { text-align: left; padding: 8px; border-bottom: 1px solid #f1f5f9; }
    th { color: #64748b; font-weight: 600; }
    td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
    .aviso { color: #b45309; }
    .erro { color: #b91c1c; }
    .rodape { color: #94a3b8; font-size: 12px; }
  </style>
</head>
<body>
  <h1 data-texto="titulo_pagina"></h1>
  <div class="sub" id="contexto">Carregando…</div>
${semDicionario}${medidas.length ? `  <section class="bloco" data-bloco="numeros">
    <h2 data-texto="numeros.titulo"></h2>
    <div class="cartoes" id="cartoes"></div>
  </section>
` : ''}${medidas.length && coluna ? `  <section class="painel" data-bloco="grafico">
    <h2 data-texto="grafico.titulo"></h2>
    <canvas id="canvas-grafico" height="110"></canvas>
  </section>
  <section class="painel" data-bloco="tabela">
    <h2 data-texto="tabela.titulo"></h2>
    <table id="tabela"><thead></thead><tbody></tbody></table>
  </section>
` : ''}  <p class="rodape" data-texto="nota_rodape"></p>
  <script>
    // Erro de consulta: o SDK mostra a mensagem dentro do bloco. Só desenha os blocos que estão no portal-relatorio: o portal pode tirar ou esconder.
    function existe(id) { return Object.prototype.hasOwnProperty.call(Portal.config.blocos, id); }

    Portal.pronto.then(function (ctx) {
      document.getElementById("contexto").textContent = ctx.cliente + (ctx.fonte === "amostra" ? " · dados de exemplo" : "");

      // Cartões: só medidas, sem coluna — uma linha agregada.
      if (existe("numeros")) {
        var numeros = Portal.bloco("numeros");
        numeros.desenhar(function (linhas) {
          var linha = linhas[0] || {};
          document.getElementById("cartoes").innerHTML = numeros.apelidos.map(function (a) {
            return '<div class="cartao"><div class="rotulo"></div><div class="valor"></div></div>';
          }).join("");
          var cartoes = document.querySelectorAll("#cartoes .cartao");
          numeros.apelidos.forEach(function (a, i) {
            cartoes[i].querySelector(".rotulo").textContent = numeros.rotulo(a);
            cartoes[i].querySelector(".valor").textContent = numeros.formatar(a, linha[a]);
          });
        });
      }

      // Gráfico: a medida principal pelo eixo.
      if (existe("grafico")) {
        var grafico = Portal.bloco("grafico");
        var chart = null;
        // desenhar roda de novo a cada filtro: o gráfico anterior sai antes.
        grafico.desenhar(function (linhas) {
          if (chart) chart.destroy();
          chart = new Chart(document.getElementById("canvas-grafico"), {
            type: "bar",
            data: {
              labels: linhas.map(function (l) { return l.eixo; }),
              datasets: [{ label: grafico.rotulo("valor"), data: linhas.map(function (l) { return l.valor; }), backgroundColor: Portal.paleta()[0] }]
            },
            options: {
              plugins: { legend: { display: false }, tooltip: { callbacks: { label: function (c) { return grafico.rotulo("valor") + ": " + grafico.formatar("valor", c.raw); } } } },
              scales: { y: { ticks: { callback: function (v) { return grafico.formatar("valor", v); } } } }
            }
          });
        });
      }

      // Tabela: o eixo e todas as medidas do bloco.
      if (existe("tabela")) {
        var tabela = Portal.bloco("tabela");
        tabela.desenhar(function (linhas) {
          var corpo = document.querySelector("#tabela tbody");
          var cabeca = document.querySelector("#tabela thead");
          corpo.textContent = "";
          cabeca.textContent = "";
          if (linhas.length === 0) { corpo.innerHTML = '<tr><td class="aviso">Nenhum dado neste recorte.</td></tr>'; return; }
          var cab = document.createElement("tr");
          [["eixo", false]].concat(tabela.apelidos.map(function (a) { return [a, true]; })).forEach(function (p) {
            var th = document.createElement("th");
            th.textContent = tabela.rotulo(p[0]);
            if (p[1]) th.className = "num";
            cab.appendChild(th);
          });
          cabeca.appendChild(cab);
          linhas.forEach(function (l) {
            var tr = document.createElement("tr");
            var td = document.createElement("td");
            td.textContent = l.eixo;
            tr.appendChild(td);
            tabela.apelidos.forEach(function (a) {
              var n = document.createElement("td");
              n.className = "num";
              n.textContent = tabela.formatar(a, l[a]);
              tr.appendChild(n);
            });
            corpo.appendChild(tr);
          });
        });
      }
    });
  </script>
</body>
</html>
`
}
